import type {
  Dimension,
  Entry,
  EntryDetail,
  EntryDimension,
  Evidence,
  LevelDescriptor,
  ReportBundle,
} from '../../shared/types.js';
import type {
  CreateEntryInput,
  CreateEvidenceInput,
  ListEntriesQuery,
  ReportQuery,
  UpdateConfigInput,
  UpdateEntryInput,
  UpsertEntryDimensionInput,
  UpsertLevelDescriptorInput,
} from '../../shared/schemas.js';

class ApiError extends Error {}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`/api${path}`, {
    method,
    headers: body !== undefined ? { 'content-type': 'application/json' } : {},
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  const data: unknown = text ? JSON.parse(text) : null;
  if (!res.ok) {
    const message =
      data && typeof data === 'object' && 'error' in data
        ? String((data as { error: unknown }).error)
        : `Request failed (${res.status})`;
    throw new ApiError(message);
  }
  return data as T;
}

function qs(q: ListEntriesQuery): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) {
    if (v !== undefined && v !== '') p.set(k, String(v));
  }
  const s = p.toString();
  return s ? `?${s}` : '';
}

export const api = {
  // Reference data
  listDimensions: () => request<Dimension[]>('GET', '/dimensions'),
  listLevelDescriptors: () => request<LevelDescriptor[]>('GET', '/level-descriptors'),
  getConfig: () => request<Record<string, string>>('GET', '/config'),
  getReport: (q: ReportQuery = {}) => {
    const p = new URLSearchParams();
    if (q.from) p.set('from', q.from);
    if (q.to) p.set('to', q.to);
    if (q.include_drafts) p.set('include_drafts', q.include_drafts);
    const s = p.toString();
    return request<ReportBundle>('GET', `/report${s ? `?${s}` : ''}`);
  },

  // Entries
  listEntries: (q: ListEntriesQuery) => request<Entry[]>('GET', `/entries${qs(q)}`),
  getEntry: (id: string) => request<EntryDetail>('GET', `/entries/${id}`),
  createEntry: (body: CreateEntryInput) => request<Entry>('POST', '/entries', body),
  updateEntry: (id: string, body: UpdateEntryInput) =>
    request<Entry>('PATCH', `/entries/${id}`, body),
  deleteEntry: (id: string) => request<void>('DELETE', `/entries/${id}`),

  // Entry <-> dimension tags
  upsertEntryDimension: (entryId: string, dimensionId: string, body: UpsertEntryDimensionInput) =>
    request<EntryDimension>('PUT', `/entries/${entryId}/dimensions/${dimensionId}`, body),
  deleteEntryDimension: (entryId: string, dimensionId: string) =>
    request<void>('DELETE', `/entries/${entryId}/dimensions/${dimensionId}`),

  // Evidence
  createEvidence: (entryId: string, body: CreateEvidenceInput) =>
    request<Evidence>('POST', `/entries/${entryId}/evidence`, body),
  deleteEvidence: (id: string) => request<void>('DELETE', `/evidence/${id}`),

  // Framework
  upsertLevelDescriptor: (dimensionId: string, level: string, body: UpsertLevelDescriptorInput) =>
    request<LevelDescriptor>('PUT', `/level-descriptors/${dimensionId}/${level}`, body),
  updateConfig: (body: UpdateConfigInput) =>
    request<Record<string, string>>('PATCH', '/config', body),
};

// Trigger a download of the full JSON export.
export function exportUrl(): string {
  return '/api/export';
}
